import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { RefreshCw, Download } from 'lucide-react';
import { toast } from 'sonner';
import { 
  tradingAccountsService, 
  TradingAccountDetails, 
  TradingAccountsStats,
  FilterOptions 
} from '@/services/admin/trading-accounts-service';
import { TradingAccountsStatsCards } from '@/components/admin/ctrader/TradingAccountsStatsCards';
import { TradingAccountsFilters } from '@/components/admin/ctrader/TradingAccountsFilters';
import { TradingAccountsTable } from '@/components/admin/ctrader/TradingAccountsTable';

export default function TradingAccountsPage() {
  const [filters, setFilters] = useState<FilterOptions>({});

  // Fetch trading accounts statistics
  const { data: stats, isLoading: isLoadingStats, refetch: refetchStats } = useQuery({
    queryKey: ['admin', 'trading-accounts', 'stats'],
    queryFn: tradingAccountsService.getAccountsStats,
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Auto-refetch every 60 seconds
  });

  // Fetch all trading accounts
  const { data: accounts, isLoading: isLoadingAccounts, refetch: refetchAccounts } = useQuery({
    queryKey: ['admin', 'trading-accounts', 'list', filters],
    queryFn: () => tradingAccountsService.getAllTradingAccounts(filters),
    staleTime: 30000, // 30 seconds  
    refetchInterval: 60000, // Auto-refetch every 60 seconds
  });

  // Fetch unique filter options
  const { data: uniqueFilters, isLoading: isLoadingFilters } = useQuery({
    queryKey: ['admin', 'trading-accounts', 'filters'],
    queryFn: tradingAccountsService.getUniqueFilters,
    staleTime: 300000, // 5 minutes
  });

  // Action handlers  
  const handleRefreshConnection = async (accountId: string) => {
    try {
      await tradingAccountsService.refreshAccountConnection(accountId);
      toast.success('Connection refresh initiated');
      await refetchData();
    } catch (error) {
      console.error('Error refreshing connection:', error);
      toast.error('Failed to refresh connection');
    }
  };

  const handleSyncBalance = async (accountId: string) => {
    try {
      await tradingAccountsService.syncAccountBalance(accountId);
      toast.success('Balance sync initiated');
      await refetchData();
    } catch (error) {
      console.error('Error syncing balance:', error);
      toast.error('Failed to sync balance');
    }
  };

  const handleDisconnectAccount = async (accountId: string) => {
    try {
      await tradingAccountsService.disconnectAccount(accountId);
      toast.success('Account disconnected');
      await refetchData();
    } catch (error) {
      console.error('Error disconnecting account:', error);
      toast.error('Failed to disconnect account');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    try {
      await tradingAccountsService.deleteAccount(accountId);
      toast.success('Account deleted');
      await refetchData();
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account');
    }
  };

  const handleBulkActions = async (accountIds: string[], action: string) => {
    try {
      const connectionIds = accountIds.map(accountId => {
        const account = accounts?.find(acc => acc.id === accountId);
        return account?.api_connection.id;
      }).filter(Boolean) as string[];

      if (action === 'refresh') {
        await tradingAccountsService.refreshMultipleAccountConnections(accountIds);
        toast.success(`Refresh initiated for ${accountIds.length} accounts`);
      } else if (action === 'sync') {
        await tradingAccountsService.syncMultipleAccountBalances(accountIds);
        toast.success(`Balance sync initiated for ${accountIds.length} accounts`);
      } else if (action === 'disconnect') {
        const result = await tradingAccountsService.bulkDisconnectApiConnections(connectionIds);
        toast.success(result.message || `Disconnected ${accountIds.length} accounts`);
      } else if (action === 'delete') {
        const result = await tradingAccountsService.bulkDeleteApiConnections(connectionIds);
        toast.success(result.message || `Deleted ${accountIds.length} accounts`);
      }
      await refetchData();
    } catch (error) {
      console.error(`Error performing bulk ${action}:`, error);
      toast.error(`Failed to ${action} accounts`);
    }
  };

  const refetchData = async () => {
    await Promise.all([refetchStats(), refetchAccounts]);
  };

  const handleRefreshAll = async () => {
    await refetchData();
    toast.success('Data refreshed');
  };

  // Export data to CSV
  const handleExportData = () => {
    if (!accounts) return;

    const csvHeaders = [
      'Account ID',
      'Account Number',
      'Account Trading ID',
      'Balance',
      'Account Type',
      'User Name',
      'User Email',
      'CSP Account Name',
      'Broker',
      'Prop Firm',
      'Connection Status',
      'Refresh Status',
      'Balance Sync Status',
      'Created Date',
      'Last Balance Update'
    ];

    const csvData = accounts.map(acc => [
      acc.id,
      acc.number_account_trading,
      acc.accountid_trading,
      acc.balance_account,
      acc.live ? 'Live' : 'Demo',
      acc.api_connection.csp_account.user.name || '',
      acc.api_connection.csp_account.user.email,
      acc.api_connection.csp_account.csp_account_name,
      acc.api_connection.csp_account.broker || '',
      acc.api_connection.csp_account.prop_firm || '',
      acc.api_connection.status,
      acc.api_connection.refresh_status || '',
      acc.api_connection.balance_sync_status || '',
      new Date(acc.created_at).toLocaleString(),
      acc.last_balance_update ? new Date(acc.last_balance_update).toLocaleString() : ''
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `trading-accounts-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Trading Accounts Management</h1>
            <p className="text-muted-foreground mt-1">
              Manage trading accounts, monitor balances, and sync account data
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button 
              onClick={handleRefreshAll}
              variant="outline" 
              size="sm"
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button 
              onClick={handleExportData}
              variant="outline" 
              size="sm"
              className="gap-2"
              disabled={!accounts || accounts.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <TradingAccountsStatsCards 
          stats={stats || {
            total_accounts: 0,
            live_accounts: 0,
            demo_accounts: 0,
            total_balance: 0,
            active_connections: 0,
            inactive_connections: 0,
            expired_connections: 0,
            recent_syncs: 0,
            failed_syncs: 0,
            unique_users: 0,
            unique_brokers: 0,
            average_balance: 0,
          }} 
          isLoading={isLoadingStats} 
        />

        {/* Filters */}
        <TradingAccountsFilters 
          filters={filters}
          onFiltersChange={setFilters}
          uniqueFilters={uniqueFilters || { brokers: [], propFirms: [], statuses: [] }}
          isLoading={isLoadingFilters}
        />

        {/* Trading Accounts Table */}
        <TradingAccountsTable 
          accounts={accounts || []}
          isLoading={isLoadingAccounts}
          onRefreshConnection={handleRefreshConnection}
          onSyncBalance={handleSyncBalance}
          onDisconnectAccount={handleDisconnectAccount}
          onDeleteAccount={handleDeleteAccount}
          onBulkActions={handleBulkActions}
        />
      </div>
    </div>
  );
}